
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch
} from 'vue'
import { useRoute } from 'vue-router'
import sentimentApi from '@/api/sentimentBarometer'
import type { SentimentRangeItem } from '@/api/sentimentBarometer'
import {
  INDEX_OPTIONS,
  formatTradingDay,
  roundSentimentScore
} from '@/utils/sentiment/constants'
import type { IndexKey, SentimentThemeMode } from '@/utils/sentiment/constants'
import { getSentimentTheme } from '@/utils/sentiment/theme'
import { loadIndexOverlay, saveIndexOverlay } from '@/utils/sentiment/storage'
import {
  mountSentimentPageShell,
  unmountSentimentPageShell
} from '@/utils/sentiment/pageShell'
import TrendChart from './components/TrendChart.vue'
import ThermometerPanel from './components/ThermometerPanel.vue'
import StageTable from './components/StageTable.vue'
import JfNoData from '@/components/jf/JfNoData.vue'

function resolveCurrentPointIndex(
  list: SentimentRangeItem[],
  activePointIndex: number
): number {
  if (!list.length) return -1
  if (activePointIndex >= 0 && activePointIndex < list.length) {
    return activePointIndex
  }
  return list.length - 1
}
console.log('window.location.href', window.location.href);
/** 设计稿基准；HEIGHT 取折中值，兼顾大屏不放大、小屏可读 */
const DESIGN_WIDTH = 1432
const DESIGN_HEIGHT = 480
const MIN_FONT_SCALE = 0.72
const MAX_FONT_SCALE = 1.08
/** 情绪趋势图与整页同比例，仅略封顶 */
const CHART_FONT_SCALE_CAP = 1.05

function calcFontScale(width: number, height: number): number {
  const scaleW = width / DESIGN_WIDTH
  const scaleH = height / DESIGN_HEIGHT
  const raw = Math.min(scaleW, scaleH)
  return Math.max(MIN_FONT_SCALE, Math.min(MAX_FONT_SCALE, raw))
}

const DEBUG_SENTIMENT_TREND = process.env.NODE_ENV === 'development'

function logSentimentTrend(label: string, payload?: unknown) {
  if (!DEBUG_SENTIMENT_TREND) return
  if (payload !== undefined) {
    console.log(`[SentimentTrend] ${label}`, payload)
  } else {
    console.log(`[SentimentTrend] ${label}`)
  }
}

export default defineComponent({
  name: 'SentimentTrend',
  components: { TrendChart, ThermometerPanel, StageTable, JfNoData },
  setup() {
    const route = useRoute()
    const routeTheme = ((route.query.theme as string) || 'dark') as SentimentThemeMode
    const viewportW = ref(
      typeof window !== 'undefined' ? window.innerWidth : DESIGN_WIDTH
    )
    const viewportH = ref(
      typeof window !== 'undefined' ? window.innerHeight : DESIGN_HEIGHT
    )

    const state = reactive({
      theme: (routeTheme === 'light' ? 'light' : 'dark') as SentimentThemeMode,
      token: (route.query.token as string) || '',
      loading: false,
      rangeList: [] as SentimentRangeItem[],
      selectedIndexes: loadIndexOverlay() as IndexKey[],
      activePointIndex: -1
    })

    const themeVars = computed(() => {
      const tokens = getSentimentTheme(state.theme)
      return {
        '--sentiment-bg': tokens.bg,
        '--sentiment-panel': tokens.panelBg,
        '--sentiment-border': tokens.border,
        '--sentiment-text': tokens.text,
        '--sentiment-text-secondary': tokens.textSecondary,
        '--sentiment-row-active': tokens.tableRowActive,
        '--sentiment-toggle-border': tokens.toggleBorder,
        '--sentiment-toggle-active': tokens.toggleActive
      }
    })

    const fontScale = computed(() => calcFontScale(viewportW.value, viewportH.value))

    const chartFontScale = computed(() =>
      Math.min(fontScale.value, CHART_FONT_SCALE_CAP)
    )

    const pageStyle = computed(() => ({
      ...themeVars.value,
      '--sentiment-scale': String(fontScale.value)
    }))

    const indexOptions = INDEX_OPTIONS

    const currentPointIndex = computed(() =>
      resolveCurrentPointIndex(state.rangeList, state.activePointIndex)
    )

    const currentScore = computed(() => {
      const idx = currentPointIndex.value
      if (idx < 0) return 0
      return roundSentimentScore(state.rangeList[idx]?.TotalScore ?? 0)
    })

    const currentTradingDay = computed(() => {
      const idx = currentPointIndex.value
      if (idx < 0) return 0
      return state.rangeList[idx]?.tradingDay ?? 0
    })

    const currentUpdateTimeText = computed(() =>
      currentTradingDay.value ? formatTradingDay(currentTradingDay.value) : '--'
    )

    const fetchRange = async () => {
      state.loading = true
      try {
        const end = new Date()
        const start = new Date()
        start.setFullYear(end.getFullYear() - 1)
        const fmt = (d: Date) =>
          `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
        const res: any = await sentimentApi.getRange({
          token: state.token,
          period: '1y',
          startDate: fmt(start),
          endDate: fmt(end)
        })
        if (res?.code === 1 && res.data) {
          const raw = Array.isArray(res.data) ? res.data : res.data.list || []
          state.rangeList = raw
            .map((item: any) => ({
              tradingDay: item.tradingDay ?? item.trading_day,
              TotalScore: Number(item.TotalScore ?? item.totalScore ?? 0),
              shChangePct: item.shChangePct ?? item.sh_pxchange,
              szChangePct: item.szChangePct ?? item.sz_pxchange,
              chiNextChangePct: item.chiNextChangePct ?? item.cyb_pxchange
            }))
            .sort((a: SentimentRangeItem, b: SentimentRangeItem) => a.tradingDay - b.tradingDay)
          state.activePointIndex = state.rangeList.length - 1
          const sample = state.rangeList[state.rangeList.length - 1]
          logSentimentTrend('fetchRange ok', {
            count: state.rangeList.length,
            selectedIndexes: [...state.selectedIndexes],
            lastDaySample: sample
              ? {
                  tradingDay: sample.tradingDay,
                  TotalScore: sample.TotalScore,
                  shChangePct: sample.shChangePct,
                  szChangePct: sample.szChangePct,
                  chiNextChangePct: sample.chiNextChangePct
                }
              : null
          })
        }
      } catch (e) {
        console.error(e)
      } finally {
        state.loading = false
      }
    }

    const toggleIndex = (key: IndexKey) => {
      const before = [...state.selectedIndexes]
      const set = new Set(state.selectedIndexes)
      if (set.has(key)) {
        set.delete(key)
      } else {
        if (set.size >= 3) {
          logSentimentTrend('toggleIndex ignored (最多 3 个指数)', { key, before })
          return
        }
        set.add(key)
      }
      state.selectedIndexes = Array.from(set) as IndexKey[]
      saveIndexOverlay(state.selectedIndexes)
      logSentimentTrend('toggleIndex', {
        key,
        before,
        after: [...state.selectedIndexes],
        activeButtons: INDEX_OPTIONS.filter((o) =>
          state.selectedIndexes.includes(o.key)
        ).map((o) => o.label)
      })
    }

    watch(
      () => state.rangeList.length,
      (len) => {
        if (len > 0 && state.activePointIndex < 0) {
          state.activePointIndex = len - 1
        }
      }
    )

    watch(
      () => state.selectedIndexes.slice(),
      (keys) => {
        logSentimentTrend('selectedIndexes changed → TrendChart props', {
          selectedIndexes: keys,
          labels: INDEX_OPTIONS.filter((o) => keys.includes(o.key)).map((o) => o.label)
        })
      },
      { immediate: true }
    )

    const onViewportResize = () => {
      viewportW.value = window.innerWidth
      viewportH.value = window.innerHeight
    }

    onMounted(() => {
      logSentimentTrend('mounted', {
        selectedIndexes: [...state.selectedIndexes],
        fromLocalStorage: loadIndexOverlay()
      })
      mountSentimentPageShell(state.theme)
      onViewportResize()
      window.addEventListener('resize', onViewportResize)
      ;(window as any).forceRefreshPage = (on_skin_changed: unknown) => {
        console.log('on_skin_changed', on_skin_changed)
        window.location.reload()
      }
      fetchRange()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onViewportResize)
      delete (window as any).forceRefreshPage
      unmountSentimentPageShell()
    })

    watch(() => state.theme, (t) => mountSentimentPageShell(t))

    return {
      ...toRefs(state),
      pageStyle,
      fontScale,
      chartFontScale,
      indexOptions,
      currentScore,
      currentTradingDay,
      currentUpdateTimeText,
      toggleIndex
    }
  }
})
